import React, { useMemo, useState } from 'react';
import { Button, Form, Input, Typography, message, Modal } from 'antd';
import { confirmPasswordReset, validatePasswordResetToken } from '../../utils/api';

const AdminResetPasswordPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const token = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('token') || '';
    } catch {
      return '';
    }
  }, []);

  React.useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await validatePasswordResetToken(token);
        if (!res.valid) {
          Modal.error({
            title: 'リンクが無効です',
            content: res.error || 'このリンクは無効または有効期限が切れています',
            onOk: () => { window.location.href = '/admin/login'; },
            centered: true,
          });
        }
      } catch {
        Modal.error({
          title: 'リンクの確認に失敗しました',
          content: 'もう一度お試しください',
          onOk: () => { window.location.href = '/admin/login'; },
          centered: true,
        });
      }
    })();
  }, [token]);

  const onSubmit = async () => {
    setLoading(true);
    try {
      const vals = await form.validateFields();
      if (!token) {
        message.error('無効なトークンです');
        setLoading(false);
        return;
      }
      await confirmPasswordReset(token, vals.newPassword);
      Modal.success({
        title: 'パスワードをリセットしました',
        content: '新しいパスワードでログインしてください。',
        onOk: () => {
          window.location.href = '/admin/login';
        },
        centered: true,
      });
      // Auto-redirect after 2 seconds
      setTimeout(() => {
        window.location.href = '/admin/login';
      }, 2000);
    } catch (e: any) {
      if (e?.errorFields) return; // validation error
      message.error(e?.response?.data?.error || 'リセットに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow p-6">
        <Typography.Title level={3} style={{ textAlign: 'center', marginBottom: 16 }}>パスワードリセット</Typography.Title>
        <Form layout="vertical" form={form} onFinish={onSubmit}>
          <Form.Item label="新しいパスワード" name="newPassword" rules={[{ required: true, message: '新しいパスワードを入力してください' }, { min: 6, message: '6文字以上で入力してください' }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item
            label="新しいパスワード（確認用）"
            name="confirmPassword"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: '新しいパスワードを確認してください' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('パスワードが一致しません'));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={loading}>パスワードをリセット</Button>
          <div className="text-right mt-3">
            <a href="/admin/login" className="text-[#0077C8]">ログインに戻る</a>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AdminResetPasswordPage;
